import { useEffect, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { locationService } from '@/services/locationService';

/**
 * Query-string keys emitted by this cascade. Server accepts them as
 * three independent filters (e.g. `?district=Amritsar&subdistrict=…`)
 * — matching the convention used by the mis-api photo/asset filters
 * (`?village=Akalgarh (167)` in the Postman collection).
 */
export const LOCATION_PARAMS = ['district', 'subdistrict', 'village'] as const;
export type LocationParam = (typeof LOCATION_PARAMS)[number];

interface ResourceLocationFilterProps {
  label: string;
  values: Record<string, string>;
  onChange: (param: LocationParam, value: string) => void;
}

/**
 * Cascading location picker — District → Subdistrict → Village.
 *
 * Reuses `locationService.getDistricts` / `getSubdistricts` /
 * `getVillages` — the same source the Asset Management module's
 * filters use — so lookup values stay consistent across the app.
 *
 * The cascade is stateful:
 *
 *   - Districts load on mount and are cached for the filter's
 *     lifetime.
 *   - Subdistricts load whenever the selected district changes;
 *     clearing the district clears both dependents.
 *   - Villages load whenever both district *and* subdistrict are
 *     selected; clearing either clears the village.
 *
 * Selecting a village emits its **village_name** (e.g.
 * `Akalgarh (167)`) as the `village` query param, matching what the
 * postman mis-api collection sends — the review endpoint filters by
 * name, not by the internal V_… id.
 *
 * The compact layout (three side-by-side selects) matches the rest
 * of the filter bar rather than the big vertical card used by
 * Asset Management's LocationFilter, so it fits inline with Search
 * / Survey Date / Assigned To without breaking the row.
 */
export function ResourceLocationFilter({
  label,
  values,
  onChange,
}: ResourceLocationFilterProps) {
  const district = values.district ?? '';
  const subdistrict = values.subdistrict ?? '';
  const village = values.village ?? '';

  const [districts, setDistricts] = useState<string[]>([]);
  const [subdistricts, setSubdistricts] = useState<string[]>([]);
  const [villages, setVillages] = useState<string[]>([]);

  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingSubdistricts, setLoadingSubdistricts] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  // Districts — one-shot on mount. `cancelled` guards state after
  // unmount to keep StrictMode double-invoke clean.
  useEffect(() => {
    let cancelled = false;
    setLoadingDistricts(true);
    locationService
      .getDistricts()
      .then((raw) => {
        if (cancelled) return;
        setDistricts(normaliseDistricts(raw));
      })
      .catch(() => {
        if (!cancelled) setDistricts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingDistricts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Subdistricts — depend on `district`.
  useEffect(() => {
    if (!district) {
      setSubdistricts([]);
      return;
    }
    let cancelled = false;
    setLoadingSubdistricts(true);
    locationService
      .getSubdistricts(district)
      .then((resp) => {
        if (cancelled) return;
        const list = resp?.data?.list ?? [];
        setSubdistricts(list.map((s) => s.subdistrict_name).filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setSubdistricts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubdistricts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [district]);

  // Villages — depend on both `district` and `subdistrict`.
  useEffect(() => {
    if (!district || !subdistrict) {
      setVillages([]);
      return;
    }
    let cancelled = false;
    setLoadingVillages(true);
    locationService
      .getVillages(district, subdistrict)
      .then((resp) => {
        if (cancelled) return;
        const list = resp?.data?.list ?? [];
        setVillages(list.map((v) => v.village_name).filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setVillages([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingVillages(false);
      });
    return () => {
      cancelled = true;
    };
  }, [district, subdistrict]);

  const handleDistrictChange = (next: string) => {
    // Cascade-clear so the request doesn't ship stale dependents.
    onChange('district', next);
    if (subdistrict) onChange('subdistrict', '');
    if (village) onChange('village', '');
  };

  const handleSubdistrictChange = (next: string) => {
    onChange('subdistrict', next);
    if (village) onChange('village', '');
  };

  const handleVillageChange = (next: string) => {
    onChange('village', next);
  };

  return (
    <fieldset className="flex flex-col text-xs">
      <legend className="mb-1 font-medium text-rgf-gray-600">{label}</legend>
      <div className="flex flex-wrap items-center gap-2">
        <LocationSelect
          value={district}
          placeholder="District"
          disabled={loadingDistricts}
          loading={loadingDistricts}
          options={districts}
          onChange={handleDistrictChange}
        />
        <LocationSelect
          value={subdistrict}
          placeholder="Subdistrict"
          disabled={!district || loadingSubdistricts}
          loading={loadingSubdistricts}
          options={subdistricts}
          onChange={handleSubdistrictChange}
        />
        <LocationSelect
          value={village}
          placeholder="Village"
          disabled={!subdistrict || loadingVillages}
          loading={loadingVillages}
          options={villages}
          onChange={handleVillageChange}
        />
      </div>
    </fieldset>
  );
}

function LocationSelect({
  value,
  placeholder,
  disabled,
  loading,
  options,
  onChange,
}: {
  value: string;
  placeholder: string;
  disabled: boolean;
  loading: boolean;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-40 appearance-none rounded-lg border border-rgf-gray-300 bg-white py-1.5 pl-2.5 pr-7 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500 disabled:cursor-not-allowed disabled:bg-rgf-gray-50 disabled:text-rgf-gray-400"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-rgf-gray-400">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </span>
    </div>
  );
}

/**
 * Districts come back from `locationService.getDistricts` in a
 * couple of shapes historically — arrays of raw strings, arrays of
 * `{ name }`, arrays of `{ district_name }`. Normalise to a plain
 * `string[]` of district names so the picker code stays simple.
 */
function normaliseDistricts(raw: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of raw) {
    if (!entry) continue;
    let name: string | undefined;
    if (typeof entry === 'string') name = entry;
    else if (typeof entry === 'object') {
      const obj = entry as Record<string, unknown>;
      name =
        (typeof obj.name === 'string' && obj.name) ||
        (typeof obj.district_name === 'string' && obj.district_name) ||
        (typeof obj.id === 'string' && obj.id) ||
        undefined;
    }
    if (name && !seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}
